/**
 * Teslyn Mobile's map: how much of the screen it takes and the Layers panel's switches (docs/specs/mobile/
 * teslyn-mobile.md §5.4).
 *
 * - R182 (3 Nov 2026): "set the map to 50% and then have a full map screen option". R200 (MB on 2026.10.20, 4 Nov
 *   2026): "the map now is only 1/3rd of the screen, it was supposed to be half". The map takes `PHONE_MAP_SHARE` of
 *   the space between the top bar and the tab bar, the list the rest; "Full screen" gives the map all of it.
 * - R201: "the layers menu doesn't open large enough to see information … we should make it 3/4 screen size". The
 *   Layers panel is an overlay of `PHONE_LAYERS_PANEL_SHARE` of that space, scrolled inside, over the map.
 * - R203: "the public air traffic layers need to be individually selectable on or off just like Teslyn Field map; we
 *   also need to expand the layers supported on the phone app and group them in sections just like Teslyn Field
 *   map". The switches are grouped in Teslyn Field's sections, in its order (`MAP_LAYER_GROUPS`), each public traffic
 *   source on its own (the sources Teslyn Cloud polls: Community ADS-B, Oz Runways and AvPlan, decision 765; OpenSky
 *   stays out of the cloud), and the pack's sections and the shape file layers each with their own switch under
 *   theirs (`PhoneMapView.off`).
 *
 * What is offered follows what the sign-in may read, as on the web: the database decides (RLS and the functions), and
 * the panel leaves out what it would refuse. A pilot-only sign-in (decision 917, R156) has no landholder boundaries
 * (`land_parcel` is staff only) and no OpenAIP airspace (the provider proxy is for staff, `integration_checkout`);
 * it has the public air traffic, the fires and the hot spots (decision 999). The state fires are offered only while
 * the organisation's state feeds are on (decision 805). Every switch is kept on the phone (`PHONE_MAP_VIEW_KEY`), one
 * set for every project.
 *
 * Not on the phone, and why (Teslyn Field's other layers): the test lines and bases and the survey line files (on the
 * laptop only), the photos (Photos tab), NOTAMs (staff only, NAIPS, decision 213), the basemag stations and the
 * observatories, the weather stations, webcams and weather tiles (the organisation's keys, through the provider
 * proxy for each tile; Space weather is on the Diurnal tab), the navaids and reporting points, and OpenSky.
 */
import { MAP_LAYER_GROUPS, type MapLayerGroup } from './map-layers.js';

// ------------------------------------------------------------------------------------------------ the map's size

/** The share of the space between the top bar and the tab bar the map takes when not full screen (R182, R200). */
export const PHONE_MAP_SHARE = 0.5;

/** The share of that space the Layers panel takes, over the map (R201: "3/4 screen size"). */
export const PHONE_LAYERS_PANEL_SHARE = 0.75;

/**
 * The map's and the list's heights in the space between the top bar and the tab bar (`space`, dp), whole pixels: the
 * map `PHONE_MAP_SHARE` of it, the list the rest; full screen, all of it.
 */
export function phoneMapSplit(space: number, full: boolean): { readonly map: number; readonly list: number } {
  const h = Math.max(0, Math.floor(space));
  if (full) return { map: h, list: 0 };
  const map = Math.round(h * PHONE_MAP_SHARE);
  return { map, list: h - map };
}

// ------------------------------------------------------------------------------------------------ the bar between

/**
 * R244: the bar between the map and its panel moves in stops, these shares of the room along it, and its default
 * (half the window's height in portrait, R217, which is more than half of this room, so the default stays a stop of
 * its own and a bar left alone changes nothing).
 */
export const PHONE_SPLIT_STOPS: readonly number[] = [0.3, 0.4, 0.5, 0.6, 0.7];
/** A stop this close to the default share is the default. */
const SPLIT_SAME = 0.06;

/** The room along the bar, and the least the map and the panel keep. */
export interface PhoneSplitRoom {
  readonly size: number;
  readonly minMap: number;
  readonly minPanel: number;
}

const inRoom = (px: number, r: PhoneSplitRoom): number =>
  Math.max(r.minMap, Math.min(r.size - r.minPanel, px));

/** The map's size for a stored share, always leaving both their least (a phone turned, the keyboard up). */
export const phoneSplitPx = (share: number, r: PhoneSplitRoom): number =>
  Math.round(inRoom(share * r.size, r));

/** The stops that fit, smallest map first; the default among them unless it does not. */
function splitStops(r: PhoneSplitRoom, defaultPx: number): number[] {
  const fits = (px: number): boolean => px >= r.minMap && px <= r.size - r.minPanel;
  const near = (px: number): boolean => Math.abs(px - defaultPx) <= SPLIT_SAME * r.size;
  return [...PHONE_SPLIT_STOPS.map((s) => s * r.size).filter((px) => fits(px) && !near(px)), defaultPx]
    .filter(fits)
    .sort((a, b) => a - b);
}

/** The stop nearest `px`, as the share to keep; null for the default, or when there is no room to choose. */
export function snapPhoneSplit(px: number, r: PhoneSplitRoom, defaultPx: number): number | null {
  const wanted = inRoom(px, r);
  const stops = splitStops(r, defaultPx);
  if (stops.length === 0) return null;
  const nearest = stops.reduce((a, b) => (Math.abs(b - wanted) < Math.abs(a - wanted) ? b : a));
  return nearest === defaultPx ? null : nearest / r.size;
}

/** The next stop from `px` towards more map (1) or more panel (-1), for VoiceOver and TalkBack. */
export function stepPhoneSplit(px: number, by: 1 | -1, r: PhoneSplitRoom, defaultPx: number): number | null {
  const stops = splitStops(r, defaultPx);
  const next = by > 0 ? stops.find((s) => s > px + 1) : [...stops].reverse().find((s) => s < px - 1);
  return next === undefined ? snapPhoneSplit(px, r, defaultPx) : snapPhoneSplit(next, r, defaultPx);
}

/** The view with the bar's place kept, per direction; null puts it back to the default. */
export function setPhoneSplit(view: PhoneMapView, axis: 'x' | 'y', share: number | null): PhoneMapView {
  const { split, sideSplit, ...rest } = view;
  const y = axis === 'y' ? share : (split ?? null);
  const x = axis === 'x' ? share : (sideSplit ?? null);
  return { ...rest, ...(y === null ? {} : { split: y }), ...(x === null ? {} : { sideSplit: x }) };
}

// ------------------------------------------------------------------------------------------------ the switches

export type PhoneLayerId =
  | 'boundaries'
  | 'planned'
  | 'states'
  | 'remaining'
  | 'pins'
  | 'pack'
  | 'fires'
  | 'hotspots'
  | 'airspace'
  | 'aerodromes'
  | 'landholders'
  | 'shapes'
  | 'aircraft'
  | 'adsb'
  | 'ozrunways'
  | 'avplan';

/** The public air traffic sources Teslyn Cloud polls (decision 765), each its own switch (R203). */
export type PhoneTrafficSource = 'adsb' | 'ozrunways' | 'avplan';
export const PHONE_TRAFFIC_SOURCES: readonly PhoneTrafficSource[] = ['adsb', 'ozrunways', 'avplan'];

export interface PhoneLayerDef {
  readonly id: PhoneLayerId;
  /** Teslyn Field's section it is listed in. */
  readonly group: MapLayerGroup;
  readonly word: string;
  /** Said under the switch. */
  readonly note: string;
  readonly defaultOn: boolean;
  /** For staff sign-ins only: what the database or the provider proxy refuses a pilot-only sign-in. */
  readonly staffOnly: boolean;
}

/** The Layers panel's switches in their sections, in Teslyn Field's order and words. */
export const PHONE_LAYERS: readonly PhoneLayerDef[] = [
  {
    id: 'boundaries',
    group: 'Survey',
    word: 'Block boundaries',
    note: 'Each block’s survey boundary.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'planned',
    group: 'Survey',
    word: 'Planned lines',
    note: 'Every planned line, dashed; patches pink and reflights yellow.',
    defaultOn: false,
    staffOnly: false,
  },
  {
    id: 'states',
    group: 'Progress',
    word: 'Line states',
    note: 'Each line in its state from Line review, with arrows the way to fly each patch and reflight.',
    defaultOn: false,
    staffOnly: false,
  },
  {
    id: 'remaining',
    group: 'Progress',
    word: 'Lines remaining',
    note: 'What is still to fly, with arrows the way to fly each patch and reflight.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'pins',
    group: 'Safety',
    word: 'Hazard pins',
    note: 'The project’s hazard pins, and those added on this phone.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'pack',
    group: 'Safety',
    word: 'Hazard pack',
    note: 'The current hazard pack; each section can be switched on its own.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'fires',
    group: 'Safety',
    word: 'Fires (state agencies)',
    note: 'Emergency WA, NSW RFS and Queensland Fire, through Teslyn Cloud.',
    defaultOn: false,
    staffOnly: false,
  },
  {
    id: 'hotspots',
    group: 'Safety',
    word: 'Satellite hot spots (DEA)',
    note: 'Heat seen from satellites in the last 24 hours, round each project in progress.',
    defaultOn: false,
    staffOnly: false,
  },
  {
    id: 'airspace',
    group: 'Aviation',
    word: 'Airspace (OpenAIP)',
    note: 'Round the project, through your organisation’s OpenAIP account in Teslyn Cloud. Not for navigation.',
    defaultOn: false,
    staffOnly: true,
  },
  {
    id: 'aerodromes',
    group: 'Aviation',
    word: 'Aerodromes',
    note: 'Aerodromes round the project, kept in the app (OurAirports). Not for navigation.',
    defaultOn: false,
    staffOnly: false,
  },
  {
    id: 'landholders',
    group: 'Reference',
    word: 'Landholder boundaries',
    note: 'The project’s landholder properties, by name.',
    defaultOn: true,
    staffOnly: true,
  },
  {
    id: 'shapes',
    group: 'Reference',
    word: 'Shape file layers',
    note: 'Added from shape files in Teslyn Field; each layer can be switched on its own.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'aircraft',
    group: 'Live',
    word: 'Our aircraft and vehicles',
    note: 'The project’s trackers, with their trails.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'adsb',
    group: 'Live',
    word: 'Community ADS-B (adsb.lol)',
    note: 'Public air traffic round the project, through Teslyn Cloud.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'ozrunways',
    group: 'Live',
    word: 'Oz Runways',
    note: 'Public air traffic round the project, through Teslyn Cloud. Tap one for its planned route.',
    defaultOn: true,
    staffOnly: false,
  },
  {
    id: 'avplan',
    group: 'Live',
    word: 'AvPlan EFB',
    note: 'Public air traffic round the project, through Teslyn Cloud. Tap one for its planned route.',
    defaultOn: true,
    staffOnly: false,
  },
];

/** The sections the panel lists, in Teslyn Field's order (only those with a switch on the phone). */
export const PHONE_LAYER_GROUPS: readonly MapLayerGroup[] = MAP_LAYER_GROUPS.filter((g) =>
  PHONE_LAYERS.some((l) => l.group === g),
);

export interface PhoneMapView {
  /** The map fills the screen (true) or takes half of it with the list under it. */
  readonly full: boolean;
  readonly layers: Readonly<Record<PhoneLayerId, boolean>>;
  /** The pack's sections and shape file layers switched off on their own (`phoneSubKey`), the rest on. */
  readonly off: readonly string[];
  /** The map's share of the room when the bar was moved (R244), over its panel; absent: the default. */
  readonly split?: number;
  /** The same for the map beside its panel (a tablet in landscape). */
  readonly sideSplit?: number;
}

/** Where the phone keeps the view (one per phone, every project). */
export const PHONE_MAP_VIEW_KEY = 'teslyn.map.view.v1';

/** Half the screen, each layer at its default: public air traffic on (Live tracking's), fires off (decision 477). */
export const PHONE_MAP_VIEW_DEFAULT: PhoneMapView = {
  full: false,
  layers: Object.fromEntries(PHONE_LAYERS.map((l) => [l.id, l.defaultOn])) as Record<PhoneLayerId, boolean>,
  off: [],
};

/** A pack section's or a shape file layer's own switch. */
export const phoneSubKey = (kind: 'pack' | 'shape', id: string): string => `${kind}:${id}`;

/** Whether a pack section or shape file layer is on (on unless switched off on its own). */
export const phoneSubOn = (view: Pick<PhoneMapView, 'off'>, key: string): boolean => !view.off.includes(key);

/** The view with one switch changed. */
export function setPhoneLayer(view: PhoneMapView, id: PhoneLayerId, on: boolean): PhoneMapView {
  return { ...view, layers: { ...view.layers, [id]: on } };
}

/** The view with a pack section's or shape file layer's own switch changed. */
export function setPhoneSub(view: PhoneMapView, key: string, on: boolean): PhoneMapView {
  const off = view.off.filter((k) => k !== key);
  return { ...view, off: on ? off : [...off, key] };
}

/** At most this many own switches are kept (a phone that has seen many projects' packs). */
const OFF_MAX = 200;

/**
 * The view kept on the phone, read back: only known switches, each a boolean; anything else is the default. A view
 * kept by 2026.10.20 (one "traffic" switch for all public air traffic) keeps its choice for each source.
 */
export function readPhoneMapView(stored: unknown): PhoneMapView {
  const v = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
  const kept = v.layers && typeof v.layers === 'object' ? (v.layers as Record<string, unknown>) : {};
  const layers: Record<PhoneLayerId, boolean> = { ...PHONE_MAP_VIEW_DEFAULT.layers };
  if (typeof kept.traffic === 'boolean') for (const s of PHONE_TRAFFIC_SOURCES) layers[s] = kept.traffic;
  for (const l of PHONE_LAYERS) {
    const x = kept[l.id];
    if (typeof x === 'boolean') layers[l.id] = x;
  }
  const off = Array.isArray(v.off)
    ? [
        ...new Set(v.off.filter((k): k is string => typeof k === 'string' && /^(pack|shape):./.test(k))),
      ].slice(-OFF_MAX)
    : [];
  const share = (x: unknown): number | null => (typeof x === 'number' && x > 0 && x < 1 ? x : null);
  const split = share(v.split);
  const sideSplit = share(v.sideSplit);
  return {
    full: v.full === true,
    layers,
    off,
    ...(split === null ? {} : { split }),
    ...(sideSplit === null ? {} : { sideSplit }),
  };
}

/** What decides which switches are offered. */
export interface PhoneLayersOffer {
  /** A pilot-only sign-in (decision 917). */
  readonly pilotOnly: boolean;
  /** The organisation's state feeds switch; null while not read. */
  readonly fireFeeds: boolean | null;
}

/**
 * The switches offered to this sign-in, in the panel's order. A switch already on stays offered (so it can be
 * switched off) while it waits to be known; the staff-only ones are never offered to a pilot-only sign-in.
 */
export function phoneLayersOffered(view: PhoneMapView, o: PhoneLayersOffer): PhoneLayerDef[] {
  return PHONE_LAYERS.filter((l) => {
    if (l.staffOnly && o.pilotOnly) return false;
    if (l.id === 'fires') return o.fireFeeds === true || view.layers.fires;
    return true;
  });
}

/** Said in the panel for what a pilot-only sign-in is not offered. */
export const PHONE_PILOT_LAYERS_NOTE =
  'Landholder boundaries and airspace are for staff sign-ins; the shape file layers show their hazards only.';

/** Said for airspace while the organisation has no OpenAIP account in Teslyn Cloud. */
export const PHONE_AIRSPACE_NOT_SET_UP =
  'Airspace needs your organisation’s OpenAIP account in Teslyn Cloud (Integrations).';

// ------------------------------------------------------------------------------------------------ public air traffic

/** The switch a public position is drawn under: its service (adsb.lol or adsb.fi are Community ADS-B), else its source. */
export function phoneTrafficSourceOf(row: {
  readonly service?: string | null;
  readonly source?: string | null;
}): PhoneTrafficSource | null {
  const s = (row.service ?? '').toLowerCase();
  if (s === 'ozrunways' || s === 'avplan') return s;
  if (s === 'adsb.lol' || s === 'adsb.fi' || s === 'adsb') return 'adsb';
  const src = (row.source ?? '').toLowerCase();
  return src === 'adsb' || src === 'ozrunways' || src === 'avplan' ? src : null;
}

/** Whether any public air traffic source is on (then the traffic is read). */
export const phoneTrafficWanted = (view: Pick<PhoneMapView, 'layers'>): boolean =>
  PHONE_TRAFFIC_SOURCES.some((s) => view.layers[s]);

/** The public positions of the sources switched on. */
export function phoneTrafficShown<
  T extends { readonly service?: string | null; readonly source?: string | null },
>(rows: readonly T[], view: Pick<PhoneMapView, 'layers'>): T[] {
  return rows.filter((r) => {
    const s = phoneTrafficSourceOf(r);
    return s !== null && view.layers[s];
  });
}
